/**
 * Web Vitals Reporting Utility
 *
 * Measures and reports Core Web Vitals metrics for performance monitoring.
 * Metrics tracked:
 * - CLS (Cumulative Layout Shift) - visual stability
 * - FCP (First Contentful Paint) - initial render speed
 * - INP (Interaction to Next Paint) - responsiveness
 * - LCP (Largest Contentful Paint) - main content load time
 * - TTFB (Time to First Byte) - server response time
 */

// Attribution build: same surface area as 'web-vitals' but metrics include an
// `attribution` field with the offending element/url/event — required to know
// WHICH DOM node is the LCP target, WHICH event caused a slow INP, etc.
import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from 'web-vitals/attribution';

export type ReportHandler = (metric: Metric) => void;

const RATING_COLORS = {
  good: '#22c55e',
  'needs-improvement': '#eab308',
  poor: '#ef4444',
} as const;

const METRIC_UNITS: Record<string, string> = {
  CLS: '', FCP: 'ms', INP: 'ms', LCP: 'ms', TTFB: 'ms',
};

// Tab-scoped session id for correlating multiple metrics from the same visit.
function getSessionId(): string {
  try {
    let id = sessionStorage.getItem('mi_wv_sid');
    if (!id) {
      id = (crypto.randomUUID?.() ?? Math.random().toString(36).slice(2)) + Date.now().toString(36);
      sessionStorage.setItem('mi_wv_sid', id);
    }
    return id;
  } catch {
    return 'no-storage';
  }
}

// LCP target element selector + URL fallback (for image LCPs).
function getLcpElementSelector(metric: Metric): string | null {
  if (metric.name !== 'LCP') return null;
  const attr = (metric as unknown as { attribution?: { element?: string; url?: string } }).attribution;
  return attr?.element ?? attr?.url ?? null;
}

// Fire-and-forget POST. sendBeacon survives page unload (critical for LCP/INP which finalize then).
function sendToBackend(metric: Metric): void {
  try {
    const payload = JSON.stringify({
      name: metric.name,
      value: metric.value,
      rating: metric.rating,
      nav_type: metric.navigationType,
      path: window.location.pathname,
      is_mobile: window.matchMedia('(max-width: 768px)').matches,
      ect: (navigator as unknown as { connection?: { effectiveType?: string } }).connection?.effectiveType ?? null,
      lcp_element: getLcpElementSelector(metric),
      session_id: getSessionId(),
      app_version: (import.meta as unknown as { env?: Record<string, string> }).env?.VITE_GIT_SHA ?? 'unknown',
    });
    const blob = new Blob([payload], { type: 'application/json' });
    const ok = navigator.sendBeacon?.('/api/internal/web-vitals', blob);
    if (!ok) {
      // sendBeacon refused (payload too big, browser policy). Fall back to keepalive fetch.
      fetch('/api/internal/web-vitals', { method: 'POST', body: payload, headers: { 'Content-Type': 'application/json' }, keepalive: true })
        .catch(() => {});
    }
  } catch {
    // Telemetry must never break the page.
  }
}

const defaultHandler: ReportHandler = (metric) => {
  const color = RATING_COLORS[metric.rating] || RATING_COLORS.poor;
  const unit = METRIC_UNITS[metric.name] || '';
  const value = metric.name === 'CLS' ? metric.value.toFixed(3) : Math.round(metric.value);
  console.log(
    `%c[Web Vitals] ${metric.name}: ${value}${unit} (${metric.rating})`,
    `color: ${color}; font-weight: bold;`
  );
  sendToBackend(metric);
};

/**
 * Reports all Core Web Vitals metrics
 *
 * @param onReport - Optional callback to handle metrics (defaults to console logging)
 *
 * @example
 * // Use default console logging
 * reportWebVitals();
 *
 * @example
 * // Send to analytics
 * reportWebVitals((metric) => {
 *   analytics.track('web_vital', {
 *     name: metric.name,
 *     value: metric.value,
 *     rating: metric.rating,
 *   });
 * });
 */
export function reportWebVitals(onReport?: ReportHandler): void {
  const handler = onReport || defaultHandler;

  // Cumulative Layout Shift - measures visual stability
  onCLS(handler);

  // First Contentful Paint - when first content renders
  onFCP(handler);

  // Interaction to Next Paint - responsiveness to user input
  onINP(handler);

  // Largest Contentful Paint - when main content is visible
  onLCP(handler);

  // Time to First Byte - server response time
  onTTFB(handler);
}

export default reportWebVitals;
